import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Loader2, LogIn, AlertCircle } from 'lucide-react'
import { useAuth } from '../lib/auth/AuthContext.jsx'

export default function LoginPage() {
  const { login, user, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/dashboard'

  const [email, setEmail] = useState('admin@keysturn.com')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.body.classList.add('is-auth-page')
    return () => document.body.classList.remove('is-auth-page')
  }, [])

  if (!isLoading && user) {
    return <Navigate to={from} replace />
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      await login(email.trim(), password)
      navigate(from, { replace: true })
    } catch (err) {
      const message = err?.uiMessage || err?.response?.data?.error || err?.message || 'Login failed'
      const friendly =
        message === 'invalid credentials'
          ? 'Wrong email or password.'
          : message === 'Network Error' || /ECONNREFUSED|Failed to fetch|timeout/i.test(message)
            ? 'Cannot reach the API server. Make sure the backend is running.'
            : message
      setError(friendly)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-bg" aria-hidden />

      <div className="auth-card">
        <div className="auth-card__brand">
          <img
            className="brand__mark brand__mark--lg"
            src="/arrow-icon.svg"
            alt=""
            width={44}
            height={44}
          />
          <div>
            <div className="auth-card__brand-title">ARrow</div>
            <div className="auth-card__brand-sub">Spatial authoring console</div>
          </div>
        </div>

        <h1 className="auth-card__title">Sign in</h1>
        <p className="auth-card__sub">Use your admin credentials to continue.</p>

        <form className="auth-form" onSubmit={onSubmit} noValidate>
          <label className="auth-field">
            <span className="auth-field__label">Email</span>
            <input
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Password</span>
            <div className="auth-field__password">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="auth-field__reveal"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error && (
            <div className="auth-error" role="alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary auth-submit"
            disabled={submitting || !email || !password}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="spin" />
                Signing in…
              </>
            ) : (
              <>
                <LogIn size={16} />
                Sign in
              </>
            )}
          </button>
        </form>

        <div className="auth-footnote">
          Prototype build · use the seeded admin account to explore.
        </div>
      </div>
    </div>
  )
}
